import axios from 'axios';
import { logout } from './ApiService';
import { defaultErrorHandler } from './ErrorHandler';
import { addBreadcrumb } from './sentry';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://127.0.0.1:3000/';

let isRefreshing = false;
let failedQueue: {
  resolve: (token: string) => void;
  reject: (error: any) => void;
}[] = [];

const processQueue = (error: any, token: string | null) => {
  failedQueue.forEach((prom) => {
    if (token) {
      prom.resolve(token);
    } else {
      prom.reject(error);
    }
  });
  failedQueue = [];
};

const axiosInstance = axios.create({
  baseURL: API_BASE_URL,
});

/**
 * Attach access token (if exists) to request headers
 */
axiosInstance.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('idToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    
    // Add Sentry breadcrumb for request
    addBreadcrumb(
      `API Request: ${config.method?.toUpperCase()} ${config.url}`,
      'http',
      'info',
      {
        method: config.method,
        url: config.url,
        baseURL: config.baseURL,
      }
    );
    
    return config;
  },
  (error) => {
    // Add breadcrumb for request error
    addBreadcrumb(
      `API Request Error: ${error.message}`,
      'http',
      'error',
      { error: error.message }
    );
    return Promise.reject(error);
  }
);

/**
 * Handle 401: attempt to refresh the token, or if refresh fails => log out + alert
 */
axiosInstance.interceptors.response.use(
  (response) => {
    // Add breadcrumb for successful response
    addBreadcrumb(
      `API Response: ${response.config.method?.toUpperCase()} ${response.config.url} - ${response.status}`,
      'http',
      'info',
      {
        method: response.config.method,
        url: response.config.url,
        status: response.status,
      }
    );
    return response;
  },
  async (error) => {
    const originalRequest = error.config;

    // Skip token refresh logic for login endpoint - let login component handle its own errors
    const isLoginEndpoint = originalRequest.url?.includes('/auth/login');
    
    // If unauthorized and we haven't retried yet (and it's not a login request)
    if (error.response?.status === 401 && !originalRequest._retry && !isLoginEndpoint) {
      originalRequest._retry = true;

      // If there's already a refresh in progress, add this request to the queue
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then((newToken) => {
            originalRequest.headers.Authorization = `Bearer ${newToken}`;
            return axiosInstance(originalRequest);
          })
          .catch((err) => Promise.reject(err));
      }

      // Otherwise, start refreshing
      isRefreshing = true;
      try {
        const refreshToken = localStorage.getItem('refreshToken');
        const userName = localStorage.getItem('userName');
        if (!refreshToken) {
          throw new Error('No refresh token found.');
        }
        if (!userName) {
          throw new Error('No user name found.');
        }

        // Attempt to refresh
        const { data } = await axios.post(`${API_BASE_URL}/auth/refresh`, {
          refreshToken,
          userName
        });

        const newIdToken = data.tokens.idToken;
        // const newRefreshToken = data.refreshToken;

        // Save new tokens
        localStorage.setItem('idToken', newIdToken);
        // localStorage.setItem('refreshToken', newRefreshToken);

        // Process queue
        processQueue(null, newIdToken);

        // Retry original
        originalRequest.headers.Authorization = `Bearer ${newIdToken}`;
        return axiosInstance(originalRequest);

      } catch (refreshError) {
        // If refresh fails, reject all queued requests
        processQueue(refreshError, null);

        // => Logout user + show alert
        logout();  // Clears tokens, etc.
        alert('Session expired or refresh token invalid. You have been logged out.');

        return Promise.reject(refreshError);

      } finally {
        isRefreshing = false;
      }
    }

    // If we get here with a 401 *again*, or any error we can't recover from,
    // we can optionally do a final logout or alert (but not for login endpoint):
    if (error.response?.status === 401 && !isLoginEndpoint) {
      // For example, if we *already retried once* and still 401 => log out
      logout();
      alert('Your session is no longer valid. Please log in again.');
    }

    // Convert error to ApiError before rejecting
    const apiError = defaultErrorHandler.handle(error);
    return Promise.reject(apiError);
  }
);

export default axiosInstance;